/**
 * Pure functions for budget master/slave constraint enforcement.
 * Single source of truth for all budget math — used by BudgetDashboard.js and tests.
 */

export const MAX_BUDGET = 10000;

/**
 * How much budget remains available for a specific category,
 * considering current allocations in ALL OTHER categories.
 *
 * @param {string} category  - The category key being queried.
 * @param {number|string} globalBudget - The master (global) budget.
 * @param {Object} categoryBudgets - Map of category → raw value (number or string).
 * @returns {number} Available budget for this category (clamped to ≥ 0).
 */
export function getRemainingForCategory(category, globalBudget, categoryBudgets) {
  const global = Math.max(0, Number(globalBudget) || 0);
  const othersTotal = Object.entries(categoryBudgets).reduce((sum, [key, val]) => {
    if (key === category) return sum;
    return sum + Math.max(0, Number(val) || 0);
  }, 0);
  return Math.max(0, global - othersTotal);
}

/**
 * Clamp a requested category budget to the maximum allowed.
 *
 * @param {string} category
 * @param {number|string} requestedValue
 * @param {number|string} globalBudget
 * @param {Object} categoryBudgets
 * @returns {number} Clamped value (0 ≤ result ≤ min(MAX_BUDGET, allowed)).
 */
export function clampCategoryBudget(category, requestedValue, globalBudget, categoryBudgets) {
  const requested = Math.max(0, Number(requestedValue) || 0);
  const allowed = getRemainingForCategory(category, globalBudget, categoryBudgets);
  return Math.min(MAX_BUDGET, requested, allowed);
}

/**
 * Maximum value a category slider may physically reach.
 */
export function categorySliderMax(category, globalBudget, categoryBudgets) {
  return Math.min(MAX_BUDGET, getRemainingForCategory(category, globalBudget, categoryBudgets));
}

/**
 * Strict interaction max for a category slider/input.
 *
 * Rule:
 * maxCategory = currentCategoryValue + max(0, globalBudget - sum(allCategories))
 *
 * This guarantees:
 * - if no budget remains globally, the category cannot increase,
 * - decreases are always possible,
 * - UI max and write-time clamping can share the same logic.
 */
export function categoryInteractionMax(category, globalBudget, categoryBudgets) {
  const current = Math.max(0, Number((categoryBudgets || {})[category]) || 0);
  const global = Math.max(0, Number(globalBudget) || 0);
  const total = totalCategoryBudget(categoryBudgets || {});
  const remaining = Math.max(0, global - total);
  return Math.min(MAX_BUDGET, current + remaining);
}

/**
 * True when sum(categories) ≤ globalBudget (with a tiny float tolerance).
 */
export function isBudgetAllocationValid(globalBudget, categoryBudgets) {
  const global = Math.max(0, Number(globalBudget) || 0);
  const total = Object.values(categoryBudgets).reduce(
    (sum, v) => sum + Math.max(0, Number(v) || 0),
    0
  );
  return total <= global + 0.01;
}

/**
 * Sum of all category budgets.
 */
export function totalCategoryBudget(categoryBudgets) {
  return Object.values(categoryBudgets).reduce(
    (sum, v) => sum + Math.max(0, Number(v) || 0),
    0
  );
}

/**
 * Proportionally scale all category budgets down so their sum equals targetGlobal.
 * Uses banker's rounding to avoid off-by-one cent errors.
 *
 * When sum(categories) ≤ targetGlobal, returns unmodified values.
 *
 * @param {Object} categoryBudgets - Map of category → raw value.
 * @param {number|string} targetGlobal
 * @returns {Object} New map of category → string (e.g. "27.03").
 */
export function rebalanceCategoryBudgets(categoryBudgets, targetGlobal) {
  const normalized = Object.fromEntries(
    Object.entries(categoryBudgets).map(([k, v]) => [k, Math.max(0, Number(v) || 0)])
  );
  const total = Object.values(normalized).reduce((s, v) => s + v, 0);
  const target = Math.max(0, Number(targetGlobal) || 0);

  if (total <= 0 || total <= target + 0.0001) {
    return Object.fromEntries(Object.entries(normalized).map(([k, v]) => [k, String(v)]));
  }

  const targetCents = Math.round(target * 100);
  const weighted = Object.entries(normalized).map(([key, amount]) => {
    const exactCents = (amount / total) * targetCents;
    const floorCents = Math.floor(exactCents);
    return { key, floorCents, fraction: exactCents - floorCents };
  });

  let distributed = weighted.reduce((s, e) => s + e.floorCents, 0);
  let remaining = Math.max(0, targetCents - distributed);
  const byFractionDesc = [...weighted].sort((a, b) => b.fraction - a.fraction);
  let idx = 0;
  while (remaining > 0 && byFractionDesc.length > 0) {
    byFractionDesc[idx % byFractionDesc.length].floorCents += 1;
    remaining -= 1;
    idx += 1;
  }

  return Object.fromEntries(
    weighted.map((e) => [e.key, String((e.floorCents / 100).toFixed(2))])
  );
}
